<script lang="ts">
	import { onMount } from 'svelte';
	// Formatters
	import { format } from 'svelte-i18n';
	// Design system components
	import { Alert, Collapse } from '@Shelly/shelly-design-system';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';
	import { mobile } from 'src/services/mobile-rework';
	import { isValidJSON } from 'src/helpers/validators';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { bulkUpdate } from 'src/components/DevicesSettings/ApplicationSettings/app_settings_helper';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	// Config
	import { BLESettingsConfig } from './weather_station_ble_settings_config';
	// Components
	import WeatherStationBLESetting from './WeatherStationBLESetting.svelte';
	import WeatherStationUnits from './WeatherStationUnits.svelte';
	// Types
	import type { Device } from 'src/types/Device';

	export let device: Device;

	let status: string = null;
	let settings = null;
	let loading = {};
	let sendedData = {};
	const _device = device;

	const connectBleDevice = () => {
		controlBleDevice.updateDeviceId(device.id);
		mobile.OS === 'IOS'
			? control_bluetooth_device_ios(device.id, 'connect')
			: control_bluetooth_device_android(device.id, 'connect');
	};

	const handleControlingDevice = (event: CustomEvent) => {
		const args = event.detail[0];
		const data = isValidJSON(args) ? JSON.parse(args) : args;
		const error = data.error;
		status = data.status;
		if (status === 'disconnectedFromDevice') {
			connectBleDevice();
		} else if (status === 'deviceProcessed') {
			if (!settings && data.device?.info?.id == device.id) {
				settings = data.device.settings;
			}
		} else {
			Object.values(BLESettingsConfig).forEach((item) => {
				if (item.success === status) {
					const param = item.parameter;
					const value = sendedData[param];
					settings[param] = value;
					loading[item.command] = false;
					bulkUpdate(device.id, { [param]: value });
					onSettingsResponse({ isok: true }, device);
				} else if (item.error === error || error === 'deviceLost') {
					if (error === 'deviceLost') {
						status = 'disconnectedFromDevice';
						connectBleDevice();
					}
					loading[item.command] = false;
					onSettingsResponse({ isok: false }, device);
				}
			});
		}
	};

	const sendData = (e: CustomEvent) => {
		const { command, parameter, value } = e.detail;
		loading[command] = true;
		sendedData[parameter] = value;
		mobile.OS === 'IOS'
			? control_bluetooth_general_ios(device.id, command, parameter, value)
			: control_bluetooth_general_android(device.id, command, parameter, value);
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('controlling_device', handleControlingDevice);
			connectBleDevice();
			return () => {
				mobile.removeEventListener('controlling_device', handleControlingDevice);
				mobile.OS === 'IOS'
					? control_bluetooth_device_ios(device.id, 'disconnect')
					: control_bluetooth_device_android(device.id, 'disconnect');
				controlBleDevice.reset();
			};
		}
	});

	const collapseUnitsMenu: any = [
		{
			component: WeatherStationUnits,
			title: $format('sub_settings_title_temperature_unit', {
				default: 'Temperature units',
			}),
			props: {
				device,
				units: 'temperature_unit',
			},
			expanded: false,
		},
		{
			component: WeatherStationUnits,
			title: $format('sub_settings_title_windspeed_units', {
				default: 'Wind speed units',
			}),
			props: {
				device,
				units: 'wind_unit',
			},
			expanded: false,
		},
		{
			component: WeatherStationUnits,
			title: $format('sub_settings_title_elevation_units', {
				default: 'Elevation units',
			}),
			props: {
				device,
				units: 'elevation_unit',
			},
			expanded: false,
		},
		{
			component: WeatherStationUnits,
			title: $format('sub_settings_title_precipitation_units', {
				default: 'Precipitation units',
			}),
			props: {
				device,
				units: 'precipitation_unit',
			},
			expanded: false,
		},
	];

	const collapseSubMenu = [
		...collapseUnitsMenu,
		...Object.keys(BLESettingsConfig).map((i) => ({
			props: { config: BLESettingsConfig[i] },
			expanded: false,
		})),
	];
</script>

{#if !mobile.isMobile}
	<Alert
		type="warning"
		isHeadingBold
		heading={$format('alert_heading_ble_devices_restricted', {
			default: 'Settings Restricted',
		})}
		message={$format('alert_message_ble_devices_restricted', {
			default:
				'Bluetooth devices can be configured only via the Shelly Smart Control application.',
		})}
		class="mb-m"
	/>
{:else if !settings}
	{#if status == 'disconnectedFromDevice'}
		<TransitionWrapper>
			<Alert
				class="mb-s"
				isHeadingBold
				heading={$format('button_press_required', {
					default: 'Please press device button.',
				})}
				message={$format('message_get_bluetooth_status', {
					default:
						'To change some settings, you need to get the latest bluetooth status of the device, by pressing the device button.',
				})}
			/>
		</TransitionWrapper>
	{:else if status === 'gettingDeviceInformation'}
		<TransitionWrapper>
			<Alert
				class="mb-s"
				heading={$format('getting_device_information', {
					default: 'Getting device information',
				})}
			/>
		</TransitionWrapper>
	{:else if status === 'buttonPressRequired' || status === 'searchingShellyDevice'}
		<TransitionWrapper>
			<Alert class="mb-s" heading={$format('press_button_required')} />
		</TransitionWrapper>
	{/if}
{/if}

{#each collapseSubMenu as { title, props, component, expanded }}
	<Collapse
		id="collapse-w0rz060"
		title={title ||
			(props?.config?.title
				? capFirstChar(
						$format(props.config.title, {
							default: props.config.title.split('_').splice(3).join(' '),
						}),
					)
				: '')}
		class="mb-s"
		{expanded}
		on:click={() => {
			collapseSubMenu.forEach((i) => (i.expanded = false));
			expanded = !expanded;
		}}
	>
		{#if component}
			<svelte:component this={component} {...props} />
		{:else}
			<WeatherStationBLESetting
				device={_device}
				config={props.config}
				{settings}
				loading={loading[props.config.command] === true}
				on:send_data={sendData}
			/>
		{/if}
	</Collapse>
{/each}
